import React from "react";
import {
  Target,
  Heart,
  Award,
  Lightbulb,
  Globe,
  CheckCircle,
  Users,
  Star,
  Building,
  Handshake,
  ExternalLink,
  Mail,
} from "lucide-react";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition inline-flex items-center justify-center ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white text-black shadow ${className}`}
  >
    {children}
  </div>
);

const Image = ({ src, alt, className = "", ...props }) => (
  <motion.img
    src={src}
    alt={alt}
    className={className}
    whileHover={{ scale: 1.03 }}
    transition={{ duration: 0.3 }}
    {...props}
  />
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-md ${className}`}
  >
    {children}
  </span>
);

const handlePartnerSubmit = async (e) => {
  e.preventDefault();

  const formData = new FormData(e.target);
  const data = Object.fromEntries(formData.entries());

  try {
    const res = await fetch("/api/partners", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    const json = await res.json();

    if (json.success) {
      alert("Demande envoyée avec succès !");
      e.target.reset();
    } else {
      alert("Erreur lors de l’envoi.");
    }
  } catch (error) {
    alert("Erreur serveur.");
  }
};

/* ============================
   MAPPING COULEURS
============================ */

const bgColorMap = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  "electric-blue": "bg-electric-blue",
};

const textColorMap = {
  primary: "text-primary",
  secondary: "text-secondary",
  "electric-blue": "text-electric-blue",
};

/* ============================
   PAGE PARTNERS
============================ */

export default function PartnersPage() {
  const { t } = useTranslation();
  const [partnerStatus, setPartnerStatus] = useState("idle");

  const partnerCategories = [
    {
      title: t("partners.categories.partners.title"),
      description: t("partners.categories.partners.description"),
      icon: Handshake,
      color: "primary",
      partners: [
        {
          name: "Club Atlas Oulmes de Badminton",
          logo: "./images/badm.png",
          description: t("partners.categories.partners.atlas.description"),
          partnership: t("partners.categories.partners.atlas.partnership"),
          since: "2025",
          website: "https://www.oulmesbadminton.com/",
        },
        {
          name: "Ministère de la Jeunesse, de la Culture et de la Communication – Département de la Jeunesse.",
          logo: "/images/minis.jpeg",
          description: t("partners.categories.partners.ministry.description"),
          partnership: t("partners.categories.partners.ministry.partnership"),
          since: "2025",
          website: "https://www.mjcc.gov.ma",
        },
      ],
    },
  ];
  /* ========================================
     AVANTAGES DES PARTENARIATS
  ========================================= */

  const partnershipBenefits = [
    {
      icon: Users,
      title: t("partners.benefits.talent.title"),
      description: t("partners.benefits.talent.description"),
    },
    {
      icon: Globe,
      title: t("partners.benefits.international.title"),
      description: t("partners.benefits.international.description"),
    },
    {
      icon: Award,
      title: t("partners.benefits.brand.title"),
      description: t("partners.benefits.brand.description"),
    },
    {
      icon: Heart,
      title: t("partners.benefits.socialImpact.title"),
      description: t("partners.benefits.socialImpact.description"),
    },
    {
      icon: Handshake,
      title: t("partners.benefits.collaboration.title"),
      description: t("partners.benefits.collaboration.description"),
    },
    {
      icon: Building,
      title: t("partners.benefits.innovation.title"),
      description: t("partners.benefits.innovation.description"),
    },
  ];

  /* ========================================
     TÉMOIGNAGES DES PARTENAIRES
  ========================================= */

  const testimonials = [
  {
    quote: t("partners.testimonials.mourad.quote"),
    author: t("partners.testimonials.mourad.author"),
    title: t("partners.testimonials.mourad.title"),
    alt: t("partners.testimonials.mourad.author"),
    company: t("partners.testimonials.company"),
    image: "./images/partenaire1.png",
  },
  {
    quote: t("partners.testimonials.othmane.quote"),
    author: t("partners.testimonials.othmane.author"),
    title: t("partners.testimonials.othmane.title"),
    alt: t("partners.testimonials.othmane.author"),
    company: t("partners.testimonials.company"),
    image: "./images/partenaire2.webp",
  },
];

  return (
    <div className="min-h-screen bg-background">
      {/* ============================
          HERO
      ============================ */}
      <section className="py-20 bg-blue-50">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-black text-foreground mb-6 leading-tight">
              {t("partners.hero.title1")}{" "}
              <span className="text-primary">
                {t("partners.hero.highlight")}
              </span>
            </h1>

            <p className="font-paragraph text-xl text-foreground/80 max-w-4xl mx-auto leading-relaxed">
              {t("partners.hero.description")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================
          CATÉGORIES DE PARTENAIRES
      ============================ */}

      <section className="py-20 bg-background">
        <div className="container max-w-[100rem] mx-auto px-6">
          {partnerCategories.map((category, index) => (
            <motion.div
              key={index}
              className="mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-4">
                  <category.icon
                    className={`h-12 w-12 ${textColorMap[category.color]} mr-4`}
                  />

                  <h2 className="font-heading text-4xl font-bold text-foreground">
                    {category.title}
                  </h2>
                </div>

                <p className="font-paragraph text-lg text-foreground/80 max-w-3xl mx-auto">
                  {category.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.partners.map((partner, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
                      <CardContent className="p-6 flex flex-col flex-grow">
                        <div className="text-center mb-4">
                          <Image
                            src={partner.logo}
                            alt={`${partner.name} logo`}
                            width={120}
                            className="h-16 w-auto mx-auto mb-4 object-contain partner-logo"
                          />

                          <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                            {partner.name}
                          </h3>

                          <div className="flex items-center justify-center gap-2 mb-3">
                            <Badge
                              className={`${bgColorMap[category.color]} text-white`}
                            >
                              {partner.partnership}
                            </Badge>

                            <Badge className="border border-gray-300">
                              {t("partners.categories.since")} {partner.since}
                            </Badge>
                          </div>
                        </div>

                        <p className="font-paragraph text-sm text-foreground/80 mb-4 text-center">
                          {partner.description}
                        </p>

                        <div className="text-center mt-auto">
                          <Button className="border border-primary text-primary hover:bg-primary hover:text-white">
                            <a
                              href={partner.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center"
                            >
                              {t("partners.categories.visitWebsite")}
                              <ExternalLink className="h-3 w-3 ml-2" />
                            </a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============================
          AVANTAGES DU PARTENARIAT
      ============================ */}

      <section className="py-20 bg-foreground text-background">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-5xl font-bold mb-6">
              {t("partners.benefits.title1")}{" "}
              <span className="text-electric-blue">
                {t("partners.benefits.highlight")}
              </span>
            </h2>

            <p className="font-paragraph text-xl text-background/80 max-w-3xl mx-auto">
              {t("partners.benefits.description")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipBenefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-background text-foreground p-6 h-full flex flex-col">
                  <CardContent className="flex flex-col flex-grow">
                    <benefit.icon className="h-12 w-12 text-primary mb-4" />

                    <h3 className="font-heading text-xl font-bold mb-3">
                      {benefit.title}
                    </h3>

                    <p className="font-paragraph text-foreground/80 flex-grow">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================
          TÉMOIGNAGES
      ============================ */}

      <section className="py-20 bg-blue-50">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-5xl font-bold text-foreground mb-6">
              {t("partners.testimonials.title1")}{" "}
              <span className="text-electric-blue">
                {t("partners.testimonials.highlight")}
              </span>{" "}
              {t("partners.testimonials.title2")}
            </h2>

            <p className="font-paragraph text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("partners.testimonials.description")}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 h-full flex flex-col">
                  <CardContent className="flex-grow flex flex-col">
                    <p className="font-paragraph text-lg text-foreground/80 italic mb-6 leading-relaxed">
                      "{testimonial.quote}"
                    </p>

                    <div className="flex items-center mt-auto">
                      <Image
                        src={testimonial.image}
                        alt={testimonial.author}
                        width={60}
                        className="w-15 h-15 rounded-full object-cover mr-4"
                      />

                      <div>
                        <h4 className="font-heading font-semibold text-foreground">
                          {testimonial.author}
                        </h4>

                        <p className="font-paragraph text-sm text-foreground/60">
                          {testimonial.title}
                        </p>

                        <p className="font-paragraph text-sm text-primary font-semibold">
                          {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================
          FORMULAIRE DE PARTENARIAT
      ============================ */}

      <section className="py-20 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-bold mb-6">
              {t("partners.form.title")}
            </h2>

            <p className="font-paragraph text-xl mb-8 max-w-3xl mx-auto">
              {t("partners.form.description")}
            </p>
          </motion.div>

          <motion.div
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="p-8 bg-background text-foreground">
              <CardContent>
                <h3 className="font-heading text-2xl font-bold text-center mb-6">
                  {t("partners.form.formTitle")}
                </h3>

                <form
                  className="space-y-6"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setPartnerStatus("loading");

                    const formData = new FormData(e.target);

                    const data = {
                      organization: formData.get("organisation"),
                      contact: formData.get("contact"),
                      email: formData.get("email"),
                      type: formData.get("orgType"),
                      partnership: formData.get("partnershipType"),
                      message: formData.get("message"),
                    };

                    try {
                      const res = await fetch("/api/partners", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(data),
                      });

                      const result = await res.json();

                      if (result.success) {
                        setPartnerStatus("success");
                        e.target.reset();
                      } else {
                        setPartnerStatus("error");
                      }
                    } catch (err) {
                      console.error(err);
                      setPartnerStatus("error");
                    }
                  }}
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-semibold mb-2">
                        {t("partners.form.organization")} *
                      </label>
                      <input
                        type="text"
                        name="organisation"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder={t("partners.form.organizationPlaceholder")}
                      />
                    </div>

                    <div>
                      <label className="block font-semibold mb-2">
                        {t("partners.form.contactPerson")} *
                      </label>
                      <input
                        type="text"
                        name="contact"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder={t("partners.form.contactPlaceholder")}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-semibold mb-2">
                        {t("partners.form.email")} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        placeholder="exemple@mail.com"
                      />
                    </div>

                    <div>
                      <label className="block font-semibold mb-2">
                        {t("partners.form.organizationType")} *
                      </label>

                      <select
                        name="orgType"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                      >
                        <option value="">{t("partners.form.choose")}</option>

                        <option value="corporate">
                          {t("partners.form.organizationTypes.corporate")}
                        </option>

                        <option value="educational">
                          {t("partners.form.organizationTypes.educational")}
                        </option>

                        <option value="nonprofit">
                          {t("partners.form.organizationTypes.nonprofit")}
                        </option>

                        <option value="government">
                          {t("partners.form.organizationTypes.government")}
                        </option>

                        <option value="foundation">
                          {t("partners.form.organizationTypes.foundation")}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">
                      {t("partners.form.partnershipType")} *
                    </label>
                    <select
                      name="partnershipType"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    >
                      <option value="">{t("partners.form.choose")}</option>

                      <option value="program-sponsor">
                        {t("partners.form.partnershipTypes.programSponsor")}
                      </option>

                      <option value="event-partner">
                        {t("partners.form.partnershipTypes.eventPartner")}
                      </option>

                      <option value="mentorship-partner">
                        {t("partners.form.partnershipTypes.mentorshipPartner")}
                      </option>

                      <option value="innovation-partner">
                        {t("partners.form.partnershipTypes.innovationPartner")}
                      </option>

                      <option value="custom">
                        {t("partners.form.partnershipTypes.custom")}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">
                      {t("partners.form.message")} *
                    </label>
                    <textarea
                      rows={4}
                      name="message"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                      placeholder={t("partners.form.messagePlaceholder")}
                    ></textarea>
                  </div>

                  <Button
                    type="submit"
                    disabled={partnerStatus === "loading"}
                    className={`w-full text-white flex items-center justify-center
    ${partnerStatus === "loading" ? "bg-gray-500 cursor-not-allowed" : "bg-primary hover:bg-primary/90"}
  `}
                  >
                    {partnerStatus === "loading" ? (
                      t("partners.form.sending")
                    ) : (
                      <>
                        <Mail className="h-5 w-5 mr-2" />
                        {t("partners.form.submit")}
                      </>
                    )}
                  </Button>

                  {partnerStatus === "success" && (
                    <p className="text-green-600 font-semibold text-center">
                      ✔️ ✔️ {t("partners.form.success")}
                    </p>
                  )}

                  {partnerStatus === "error" && (
                    <p className="text-red-600 font-semibold text-center">
                      ❌ {t("partners.form.error")}
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ============================
          APPEL À CONTACT
      ============================ */}

      <section className="py-20 bg-background">
        <div className="container max-w-[100rem] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-bold text-foreground mb-6">
              {t("partners.contact.title")}
            </h2>

            <p className="font-paragraph text-xl text-foreground/80 mb-8 max-w-3xl mx-auto">
              {t("partners.contact.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-primary hover:bg-primary/90 text-white">
                <Link to="/contact"> {t("partners.contact.contactTeam")}</Link>
              </Button>

              <Button className="border border-gray-400 text-black hover:bg-gray-200">
                <Link to="/about"> {t("partners.contact.learnMore")}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
